"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useAnalytics, getEvent } from "@/lib/analytics";
import { useReplyAttachments, useFeedbackPanelAutoOpen } from "@/entities/feature-announcement-feedback";
import { useMyFeedback, useMyFeedbackThread, useReplyToFeedback, useMarkFeedbackViewed, usePrepareReplyScreenshot } from "./feedback-conversations-hooks";
import { isTicketUnread } from "../lib/derive-unread-flag";
import { pickNewestUnread } from "../lib/pick-newest-unread";

/**
 * Central hook for the compact widget "Your feedback" tab. Single stacked column
 * that drills list -> thread -> back. Shares the same query hooks + keys as the
 * page hook, so a reply in either surface refreshes the other within the session.
 * Selected-thread state is per-surface useState (does not carry to the page in v1).
 */
export function useMyFeedbackPanel() {
  const $ = _c(38);
  const {
    track
  } = useAnalytics();
  const {
    items,
    isLoading,
    error
  } = useMyFeedback();
  const [selectedId, setSelectedId] = useState(null);
  const [locallyViewedIds, setLocallyViewedIds] = useState(_temp);
  const [replyDraft, setReplyDraft] = useState("");
  const thread = useMyFeedbackThread(selectedId);
  const reply = useReplyToFeedback();
  const markViewed = useMarkFeedbackViewed();
  const prepareScreenshot = usePrepareReplyScreenshot();
  const replyAttachments = useReplyAttachments(selectedId, prepareScreenshot);
  const {
    readyAttachments,
    clearAttachments
  } = replyAttachments;
  const {
    mutate: markFeedbackViewed
  } = markViewed;
  const {
    mutate: sendFeedbackReply
  } = reply;
  let t0;
  if ($[0] !== clearAttachments || $[1] !== markFeedbackViewed || $[2] !== track) {
    t0 = (id, friendlyStatus) => {
      setSelectedId(id);
      setReplyDraft("");
      clearAttachments();
      setLocallyViewedIds(prev => new Set(prev).add(id));
      markFeedbackViewed({
        feedbackId: id
      });
      track(getEvent("feature-announcement", "feedback_ticket_selected"), {
        feedbackId: id,
        friendlyStatus
      });
    };
    $[0] = clearAttachments;
    $[1] = markFeedbackViewed;
    $[2] = track;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const openThread = t0;
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSelectedId(null);
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const back = t1;
  let t2;
  if ($[5] !== clearAttachments || $[6] !== readyAttachments || $[7] !== replyDraft || $[8] !== selectedId || $[9] !== sendFeedbackReply || $[10] !== track) {
    t2 = featureKey => {
      const content = replyDraft.trim();
      if (!selectedId || !content && readyAttachments.length === 0) {
        return;
      }
      sendFeedbackReply({
        feedbackId: selectedId,
        content,
        attachments: readyAttachments
      }, {
        onSuccess: () => {
          setReplyDraft("");
          clearAttachments();
          track(getEvent("feature-announcement", "feedback_reply_sent"), {
            feedbackId: selectedId,
            featureKey,
            contentLength: content.length,
            attachmentCount: readyAttachments.length
          });
        }
      });
    };
    $[5] = clearAttachments;
    $[6] = readyAttachments;
    $[7] = replyDraft;
    $[8] = selectedId;
    $[9] = sendFeedbackReply;
    $[10] = track;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const sendReply = t2;
  let t3;
  if ($[12] !== items || $[13] !== locallyViewedIds) {
    let t4;
    if ($[15] !== locallyViewedIds) {
      t4 = item => ({
        item,
        unread: isTicketUnread(item, locallyViewedIds)
      });
      $[15] = locallyViewedIds;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    t3 = items.map(t4);
    $[12] = items;
    $[13] = locallyViewedIds;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const rows = t3;
  const shouldAutoOpen = useFeedbackPanelAutoOpen();
  const [autoOpened, setAutoOpened] = useState(null);
  if (autoOpened === null && shouldAutoOpen && !selectedId && rows.length > 0) {
    const target = pickNewestUnread(rows);
    if (target) {
      const id_0 = target.item.id;
      setAutoOpened({
        id: id_0,
        friendlyStatus: target.item.friendlyStatus
      });
      setSelectedId(id_0);
      setReplyDraft("");
      setLocallyViewedIds(prev_0 => new Set(prev_0).add(id_0));
    }
  }
  let t4;
  if ($[17] !== clearAttachments || $[18] !== markFeedbackViewed || $[19] !== track) {
    t4 = (id_1, friendlyStatus_0) => {
      clearAttachments();
      markFeedbackViewed({
        feedbackId: id_1
      });
      track(getEvent("feature-announcement", "feedback_ticket_selected"), {
        feedbackId: id_1,
        friendlyStatus: friendlyStatus_0
      });
    };
    $[17] = clearAttachments;
    $[18] = markFeedbackViewed;
    $[19] = track;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const recordAutoOpen = useEffectEvent(t4);
  let t5;
  if ($[21] !== autoOpened || $[22] !== recordAutoOpen) {
    t5 = () => {
      if (autoOpened) {
        recordAutoOpen(autoOpened.id, autoOpened.friendlyStatus);
      }
    };
    $[21] = autoOpened;
    $[22] = recordAutoOpen;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== autoOpened) {
    t6 = [autoOpened];
    $[24] = autoOpened;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  useEffect(t5, t6);
  let t7;
  if ($[26] !== error || $[27] !== isLoading || $[28] !== openThread || $[29] !== reply.error || $[30] !== reply.isPending || $[31] !== replyAttachments || $[32] !== replyDraft || $[33] !== rows || $[34] !== selectedId || $[35] !== sendReply || $[36] !== thread) {
    t7 = {
      rows,
      isLoading,
      error,
      selectedId,
      openThread,
      back,
      thread,
      replyDraft,
      setReplyDraft,
      sendReply,
      isReplying: reply.isPending,
      replyError: reply.error,
      replyAttachments
    };
    $[26] = error;
    $[27] = isLoading;
    $[28] = openThread;
    $[29] = reply.error;
    $[30] = reply.isPending;
    $[31] = replyAttachments;
    $[32] = replyDraft;
    $[33] = rows;
    $[34] = selectedId;
    $[35] = sendReply;
    $[36] = thread;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  return t7;
}
function _temp() {
  return new Set();
}